<template>
    <div class="container mx-auto p-4 max-w-7xl">
        <h1 class="font-bold mb-4 text-center text-4xl text-primary py-7 mt-10">
            قائمة المشاريع
        </h1>

        <!-- أزرار الفلاتر -->
        <div class="flex justify-center flex-col md:flex-row gap-4 mb-6 rounded-lg border-primary border-separate">
            <button
                @click="filterCompletedProjects"
                class="px-4 py-2 rounded-lg transition-colors"
                :class="filter === 'completed' 
                    ? 'bg-primary dark:bg-primary text-white' 
                    : 'bg-gray-600 dark:bg-white dark:text-blue-700 text-white'"
            >
                {{ t('filterProject.Completed') }}
            </button>

            <button
                @click="filterOngoingProjects"
                class="px-4 py-2 rounded-lg transition-colors"
                :class="filter === 'ongoing' 
                    ? 'bg-primary dark:bg-primary text-white' 
                    : 'bg-gray-600 dark:bg-white dark:text-gray-500 text-white'"
            >
                {{ t('filterProject.ongoing') }}
            </button>

            <button
                @click="resetFilter"
                class="px-4 py-2 rounded-lg transition-colors"
                :class="filter === 'all' 
                    ? 'bg-primary dark:bg-primary text-white' 
                    : 'bg-gray-600 dark:bg-white dark:text-gray-500 text-white'"
            >
                <p
                  
                    :class="filter === 'all' ? 'dark:text-white' : 'dark:text-blue-700'" 
                >
                {{ t('filterProject.all') }}
            </p>
            </button>
        </div>

        <!-- عرض المشاريع -->
        <div v-if="!loading && !error" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div v-for="project in visibleProjects" :key="project.id" class="rounded-lg shadow-xl my-4 p-4 bg-white dark:bg-secondaryDark">
                <img :src="project.main_image" alt="Project image" class="w-full h-56 object-cover mb-4">
                <div class="flex items-center justify-between self-center">
                    <!-- اسم المشروع -->
                    <h2 class="text-xl font-bold mb-2 dark:text-white opacity-75 flex-shrink-0 w-3/4">
                        {{ project.name }}
                    </h2>
                    <!-- زر عرض التفاصيل -->
                    <router-link :to="'/projects/' + project.id"
                        class="text-primary dark:text-primary hover:underline hover:text-gray-dark text-lg flex-shrink-0 w-1/4">
                        {{ t('projects.view') }}
                    </router-link>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import { useRoute } from 'vue-router';
import { BASE_URL } from '@/config';
import { useI18n } from 'vue-i18n';

const { t, locale } = useI18n();
const projects = ref([]);
const loading = ref(true);
const error = ref(null);
const route = useRoute();
const isImageLoading = ref({});
const visibleProjectsCount = ref(9);
const filter = ref('all'); // الفلتر الحالي

// الفلاتر
const visibleProjects = computed(() => {
    if (filter.value === 'completed') {
        return projects.value.filter(project => project.ending_date);
    } else if (filter.value === 'ongoing') {
        return projects.value.filter(project => project.starting_date && !project.ending_date);
    }
    return projects.value;
});

// دالة لجلب المشاريع
async function fetchProjects() {
    loading.value = true;
    try {
        const response = await fetch(`${BASE_URL}/projects`, {
            headers: {
                'Accept-Language': locale.value,
            },
        });
        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const data = await response.json();
        projects.value = data.data;

        // تعيين حالة تحميل الصور لكل مشروع
        projects.value.forEach((project) => {
            isImageLoading.value[project.id] = true; // تعيين حالة تحميل الصورة إلى true عند البدء
        });

        // تحميل الصور مسبقًا
        await Promise.all(projects.value.map(project => preloadImage(project.main_image, project.id)));

    } catch (err) {
        console.error('Error fetching projects:', err);
        error.value = err.message;
    } finally {
        loading.value = false;
    }
}

// دالة لتحميل الصور مسبقًا
function preloadImage(url, projectId) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = url;
        img.onload = () => {
            handleImageLoad(projectId); // تحديث حالة تحميل الصورة عند نجاح التحميل
            resolve();
        };
        img.onerror = () => {
            handleImageError(projectId); // التعامل مع الخطأ إذا لم تتمكن الصورة من التحميل
            reject();
        };
    });
}

// دالة التعامل مع تحميل الصورة بنجاح
function handleImageLoad(projectId) {
    isImageLoading.value[projectId] = false; // تعيين حالة تحميل الصورة إلى false عند اكتمال التحميل
}

// دالة التعامل مع خطأ تحميل الصورة
function handleImageError(projectId) {
    isImageLoading.value[projectId] = false; // تعيين حالة تحميل الصورة إلى false في حال حدوث خطأ
}

// التحقق من المشروع النشط
function isActiveProject(projectId) {
    return projectId === Number(route.params.id);
}

// دالة عرض المزيد من المشاريع
function showMoreProjects() {
    visibleProjectsCount.value += 9;
}

// دوال الفلاتر
function filterCompletedProjects() {
    filter.value = 'completed';
}

function filterOngoingProjects() {
    filter.value = 'ongoing';
}

function resetFilter() {
    filter.value = 'all';
}

// جلب المشاريع عند تحميل الصفحة
onMounted(() => {
    fetchProjects();
});

// جلب المشاريع عند تغيير اللغة أو الرابط
watch([locale, route], () => {
    fetchProjects();
});
</script>

<style scoped>
.container {
    margin-top: 50px;
}
.grid {
    gap: 20px;
}

/* تحسين تجربة المستخدم للأزرار */
button:focus {
    @apply outline-none ring-2 ring-primary ring-opacity-50;
}
</style>